import { useEffect, useRef } from 'react';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { NoopAlgorithm } from './NoopAlgorithm';
import { clusterRenderer, retireClusterer, diffMarkers } from './MarkerCluster';

/**
 * Owns the regular/expanded/selected order-bucket clusterers in one effect so
 * cross-bucket marker migrations can be sequenced: removeMarker unmaps a marker
 * even with noDraw, so every removal must run before any gaining clusterer
 * renders — otherwise a marker migrating buckets is left hidden.
 */
function MarkerClusterGroups({ map, regular, expanded, selected }) {
    const clustersRef = useRef({});

    useEffect(() => {
        if (!map.current) {
            return;
        }

        const groups = [
            ['regular', regular],
            ['expanded', expanded],
            ['selected', selected],
        ];

        const deltas = groups.map(([name, group]) => {
            // Algorithm choice follows the truthiness of `disabled`; recreate the
            // clusterer only when that flips.
            const noop = !!group.disabled;
            let entry = clustersRef.current[name];

            if (!entry || entry.noop !== noop) {
                if (entry) {
                    retireClusterer(entry.clusterer);
                }

                entry = {
                    clusterer: new MarkerClusterer({
                        map: map.current,
                        renderer: clusterRenderer(group.color || 'black'),
                        onClusterClick: group.onClusterClick ? group.onClusterClick : undefined,
                        ...(noop
                            ? {
                                  algorithm: new NoopAlgorithm(),
                              }
                            : {}),
                    }),
                    noop,
                    prevSet: new Set(),
                };
                clustersRef.current[name] = entry;
            }

            const { nextSet, added, removed } = diffMarkers(entry.prevSet, group.markers || []);
            entry.prevSet = nextSet;

            return { entry, added, removed };
        });

        // All removals across every clusterer first, then all additions.
        deltas.forEach(({ entry, removed }) => {
            if (removed.length) {
                entry.clusterer.removeMarkers(removed, true);
            }
        });
        deltas.forEach(({ entry, added }) => {
            if (added.length) {
                entry.clusterer.addMarkers(added, true);
            }
        });

        // Render every changed clusterer TWICE. A clusterer's render() unmaps
        // any marker that was in its previous cluster set but not its current
        // markers — so when the SAME marker instance migrates buckets (an order
        // marker moving selected -> regular when its selection clears on assign),
        // the losing clusterer's render can unmap a marker the gaining clusterer
        // just placed, depending on render order. The map only re-renders on the
        // next `idle` event, so the marker stays hidden until the user pans/zooms.
        // After the first pass every clusterer's internal cluster set matches its
        // current markers, so the second pass re-places any migrated marker with
        // no clusterer left to unmap it.
        const changed = deltas.filter(({ added, removed }) => added.length || removed.length);
        changed.forEach(({ entry }) => entry.clusterer.render());
        changed.forEach(({ entry }) => entry.clusterer.render());
    }, [
        map.current,
        regular.markers,
        regular.disabled,
        expanded.markers,
        expanded.disabled,
        selected.markers,
        selected.disabled,
    ]);

    return null;
}

export default MarkerClusterGroups;
